import React from 'react';
import {Box, Container, Stack, Typography, useMediaQuery, useTheme} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import Banner from '../components/Banner.js';
import FeedbackCarousel from '../components/FeedbackCarousel.js';
import {imagekitStaticURL} from '../util/util.js';
import {withTitle} from '../title.js';
import withMainContainer from '../components/WithMainContainer.js';
import CardContainer from '../components/CardContainer.js';
import WhatsAppButton from '../components/WhatsAppButton.js';

const WHATSAPP_MESSAGE = `היי סימונה, אשמח לקבל פרטים על ליווי עסקי איתך 🌸`;

const CheckLine = ({children, color='primary'})=><Stack direction="row"
  alignItems="center" gap={1}>
  <CheckIcon color={color}/>
  {children}
</Stack>;

const Counseling = ()=>{
    let theme = useTheme();
    let sm = useMediaQuery(theme.breakpoints.down('sm'));
    let md = useMediaQuery(theme.breakpoints.down('md'));
    return <Stack direction="column" gap={3}>
      <Banner image={imagekitStaticURL`work-desk.png`}
        placeholderColor="#242328" backgroundPosition="left">
        <Typography variant="h4" textAlign="center">
          ליווי עסקי ואסטרטגיה הוליסטית
        </Typography>
        <Typography textAlign="center">
          <strong>עסק טוב לא נבנה מהעתק-הדבק, הוא נבנה ממך.</strong><br/>
          אם יש לך עסק - או רצון להקים אחד - ואת מרגישה שזה לא ממש זורם כמו שציפית, אם את עושה, משתדלת, לומדת ומשקיעה…<br/>
          ועדיין התוצאות לא מגיעות - או שאולי את רק בתחילת הדרך ולא בטוחה מה הצעד הבא -
        </Typography>
        <Typography variant="h5" textAlign="center">
          זה הזמן לעשות את זה אחרת!
        </Typography>
      </Banner>
      <Container maxWidth="lg">
        <Stack direction="column" gap={3}>
          <Stack gap={4} justifyContent="center"
            direction={sm ? 'column' : 'row'}
            alignItems={sm ? 'center' : null}>
            <Box component="img" width="250px" height="250px"
              src={imagekitStaticURL`hollistic-strategy.jpg`} sx={{
                  objectFit: 'contain',
                  boxShadow: '4px 4px 8px rgba(0, 0, 0, 0.5)',
                  border: '4px solid white',
                  borderRadius: '4px',
            }}/>
            <Stack direction="column" gap={3} justifyContent="center">
              <Typography textAlign={sm ? 'center' : null}>
                אני לא עוד יועצת עסקית עם תוכנית גנרית.<br/>
                אני מציעה ליווי עסקי עם נשמה - תהליך שמשלב כלים עסקיים וטכניים עם חיבור עמוק
                למה ש<em>את</em> באת לעשות פה.
              </Typography>
              <Typography variant="h6" textAlign={sm ? 'center' : null}>
                כזה שרואה את כל התמונה - כולל מה שלא תמיד רואים לבד.
              </Typography>
              <Typography textAlign={sm ? 'center' : null}>
                יחד נבנה אסטרטגיה שמתאימה רק לך - כזו שמבוססת על מי שאת באמת, על החוזקות שלך, ועל הדרך שהכי טבעית עבורך להצליח.
              </Typography>
            </Stack>
          </Stack>
          <Stack justifyContent="center" alignItems="center">
            <Box component="img" maxWidth="200px"
              src={imagekitStaticURL`moon-divider.png`} sx={{objectFit: 'contain'}}/>
          </Stack>
        </Stack>
      </Container>
      <Stack direction="column" justifyContent="center" gap={3}
        marginTop={2} paddingTop={4} paddingBottom={4}
        sx={{backgroundColor: theme.palette.primary.light}}>
        <Typography variant="h5" textAlign="center">
          מה הליווי הזה נותן לך?
        </Typography>
        <Typography textAlign="center">
          להבין מה את באמת רוצה מהעסק שלך, ומה חשוב לך לא לוותר עליו.<br/>
          לזקק את החזון שלך ולבנות מטרות ברורות שיתורגמו לעשייה בשטח.<br/>
          לבנות שיווק שמתאים לך - כזה שיביא לקוחות בלי שתצטרכי להעמיד פנים.<br/>
        </Typography>
        <Box margin="auto" padding={2}>
          <CardContainer>
            <Stack direction="column" gap={3} padding={4} maxWidth={600}>
              <Typography variant="h6">
                ולצד זה - כל הבסיס העסקי והטכני שאת צריכה כדי להפוך את הפאשן שלך לעסק מכניס ורווחי:
              </Typography>
              <CheckLine>
                לבנות משפך שיווקי מדויק לעסק שלך
              </CheckLine>
              <CheckLine>
                לזהות את קהל היעד שלך ולבנות מסרים שמדברים אליו בדיוק
              </CheckLine>
              <CheckLine>
                לסדר מחירון נכון שיאזן בין ערך לרווחיות
              </CheckLine>
              <CheckLine>
                לבנות הצעות, מסלולים, ותהליכי מכירה
              </CheckLine>
              <CheckLine>
                ליצור תוכן שמתאים לקהל שלך - ולך
              </CheckLine>
              <CheckLine>
                להבין מה עובד במדיה החברתית, ולנהל את הזמן והתוכן שלך בקלות
              </CheckLine>
              <CheckLine>
                להשתמש בכלים כמו קאנבה, CapCut, אפליקציות AI ועוד - כדי לחסוך זמן ואנרגיה
              </CheckLine>
            </Stack>
          </CardContainer>
        </Box>
      </Stack>
      <Container maxWidth="md">
        <Stack gap={5} justifyContent="center"
          direction={sm ? 'column' : 'row'}
          alignItems={sm ? 'center' : null}
          paddingTop={3} paddingBottom={3}>
          <Stack direction="column" gap={4}>
            <Typography variant="h5" textAlign={sm ? 'center' : null}>
              אני לא מגיעה עם תבנית אחת שמתאימה לכולם - אני רואה אותך, את הכישרון שלך, ואת מה שצריך לקרות כדי שהעסק יתחיל לשרת אותך.
            </Typography>
            <Typography textAlign={sm ? 'center' : null}>
              וכל זה קורה בשילוב בין ידע עסקי, אסטרטגיה וטכנולוגיה - לבין כלים רוחניים שמוסיפים רמות עומק אחרות:<br/>
              טארוט, נומרולוגיה ואסטרולוגיה - כדי לא רק לתכנן, אלא גם לדייק, לקצר תהליכים ולשחרר חסמים.
            </Typography>
          </Stack>
          <Box component="img" height="150px"
            src={imagekitStaticURL`zen-lady-2.png`} sx={{objectFit: 'contain'}}/>
        </Stack>
      </Container>
      <Banner image={imagekitStaticURL`work-desk-n-coffee.png`}
        placeholderColor="#242328">
        <Typography variant="h5">
          איך זה נראה בפועל?
        </Typography>
        <Typography>
          זו תוכנית של 10 מפגשים (ואפשר גם פחות - לפי הצורך שלך), שבה אנחנו עובדות יחד על כל חלק בעסק:
        </Typography>
        <Stack direction="column" gap={2} alignItems="start" justifyContent="start">
          <CheckLine color="contrastText">
            הגדרת חזון ברור וערכים שלא מתפשרים עליהם
          </CheckLine>
          <CheckLine color="contrastText">
            מיפוי קהל יעד ובניית שפה שיווקית
          </CheckLine>
          <CheckLine color="contrastText">
            סידור מחירון, ניהול הצעות ושיחות מכירה
          </CheckLine>
          <CheckLine color="contrastText">
            ניהול תוכן ומדיה - הכל על אינסטגרם, ביו, סטוריז, תכנון פוסטים ועוד
          </CheckLine>
          <CheckLine color="contrastText">
            בניית אסטרטגיית שיווק והשקות
          </CheckLine>
          <CheckLine color="contrastText">
            תכנון הצעות פרימיום או שירותים חדשים
          </CheckLine>
          <CheckLine color="contrastText">
            שימוש בכלים טכניים חכמים (קאנבה, AI, ניוזלטרים, אוטומציות ועריכת סרטונים)
          </CheckLine>
          <CheckLine color="contrastText">
            קריאה קלטית עסקית בטארוט שנותנת מבט עומק שנתי - כדי להבין מה מחכה לנו השנה ואיך הכי נכון לפעול כדי להגיע לתוצאות
          </CheckLine>
        </Stack>
      </Banner>
      <Container maxWidth="md">
        <Stack direction="column" gap={3} alignItems="center">
          <Typography variant="h4" textAlign="center">
            מה עכשיו?
          </Typography>
          <Typography textAlign="center">
            מרגישה שזה בדיוק מה שחיפשת?<br/>
            שלחי לי הודעת וואטסאפ עם כמה מילים על העסק שלך - ונקבע שיחת התאמה אישית, בלי התחייבות וללא עלות.<br/>
            נדבר, נרגיש, ונבין יחד האם הליווי הזה מתאים לך - ואיך זה יכול להיראות בדיוק בשבילך.
          </Typography>
          <WhatsAppButton message={WHATSAPP_MESSAGE} text="לקביעת שיחת התאמה ללא עלות" variant="contained"/>
        </Stack>
      </Container>
      <Stack width="100%" sx={{backgroundColor: theme.palette.primary.dark}} marginTop={4} paddingBottom={4}>
        <Container maxWidth="lg">
          <Typography color={theme.palette.primary.contrastText} align="center" variant="h3" marginTop={4}>
            מה לקוחות מספרות
          </Typography>
          <FeedbackCarousel tags="business-feedback"/>
          <Stack direction={sm ? 'column' : 'row'} gap={3}
            justifyContent="center" alignItems="center">
            <iframe style={{border: 'none'}}
              width={md ? '250' : '400'}
              height="300"
              src="https://www.youtube.com/embed/GYODriVL0sA"></iframe>
            <iframe style={{border: 'none'}}
              width={md ? '250' : '400'}
              height="300"
              src="https://www.youtube.com/embed/Jja4gZ7xlRY"></iframe>
          </Stack>
        </Container>
        <Stack justifyContent="center" alignItems="center" marginBottom={4} marginTop={4}>
          <WhatsAppButton message={WHATSAPP_MESSAGE} text="לקביעת שיחת התאמה ללא עלות" variant="contained"/>
        </Stack>
      </Stack>
    </Stack>;
};

export default withMainContainer(withTitle(`סימונה צ'רי - ליווי עסקי ואסטרטגיה הוליסטית`, Counseling), {headerMargin: 0});
